import { useCallback, useEffect, useState } from "react";
import { CalendarClock, CircleDollarSign, Copy, Gift, KeyRound, Loader2, Mail, Search, ShieldCheck, UserSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Broker directory (super_admin + support). Supports search + row
 * actions for impersonation and subscription overrides.
 */
export default function AdminBrokerDirectoryPage() {
  const { adoptImpersonation, user } = useAuth();
  const canReset =
    user?.role === "super_admin" || user?.role === "support" || user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrideKind, setOverrideKind] = useState(null); // 'pro' | 'trial' | 'referral'
  const [overrideForm, setOverrideForm] = useState({ tier_id: "tier_monthly", months: 1, days: 7 });
  const [busy, setBusy] = useState(false);
  const [resetBusyId, setResetBusyId] = useState(null);
  // Temp-password reveal state — shown ONCE per set-temp-password action.
  const [tempPwTarget, setTempPwTarget] = useState(null);
  const [tempPwValue, setTempPwValue] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load users"));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const impersonate = async (row) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${row.id}`);
      adoptImpersonation(data);
      toast.success(`Impersonating ${row.full_name || row.email}`);
      window.location.assign("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err, "Impersonation failed"));
    }
  };

  const applyOverride = async () => {
    if (!overrideTarget) return;
    setBusy(true);
    try {
      if (overrideKind === "pro") {
        await api.post(`/admin/users/${overrideTarget.id}/grant-pro`, {
          tier_id: overrideForm.tier_id,
          months: Number(overrideForm.months),
        });
        toast.success("Pro granted");
      } else if (overrideKind === "trial") {
        await api.post(`/admin/users/${overrideTarget.id}/extend-trial`, {
          days: Number(overrideForm.days),
        });
        toast.success("Trial extended");
      } else if (overrideKind === "referral") {
        await api.post(`/admin/users/${overrideTarget.id}/referral-credit`);
        toast.success("Referral credit granted (+30 days)");
      }
      setOverrideTarget(null);
      setOverrideKind(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Override failed"));
    } finally {
      setBusy(false);
    }
  };

  const sendResetEmail = async (row) => {
    setResetBusyId(row.id);
    try {
      const { data } = await api.post(`/admin/users/${row.id}/send-reset`);
      if (data?.branch === "google_only") {
        toast.success(
          `${row.full_name} signs in with Google — sent them a reminder.`,
        );
      } else if (data?.branch === "rate_capped") {
        toast.error(
          "This account has hit the hourly reset cap — please try again later.",
        );
      } else {
        toast.success(`Reset link emailed to ${row.email}.`);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Send reset failed"));
    } finally {
      setResetBusyId(null);
    }
  };

  const setTempPassword = async (row) => {
    setResetBusyId(row.id);
    try {
      const { data } = await api.post(`/admin/users/${row.id}/set-temp-password`);
      setTempPwTarget(row);
      setTempPwValue(data?.temporary_password || "");
    } catch (err) {
      toast.error(formatApiError(err, "Temp password failed"));
    } finally {
      setResetBusyId(null);
    }
  };

  const copyTempPw = async () => {
    if (!tempPwValue) return;
    try {
      await navigator.clipboard.writeText(tempPwValue);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-6 py-12 md:px-10" data-testid="admin-broker-directory">
      <div className="mb-rise">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/15 bg-brand-navy/5 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy">
          <UserSquare className="h-3 w-3" />
          Broker directory
        </div>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          User lookup & support tools
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-gray-500">
          Search by name, email, or PRC. Actions include impersonation and
          subscription overrides (audit-logged).
        </p>
      </div>

      <div className="mt-8 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search brokers…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9"
            data-testid="admin-user-search"
          />
        </div>
        <Button onClick={load} data-testid="admin-user-search-submit">Search</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-mb-card">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">No brokers found.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-user-table">
            <thead className="border-b border-gray-100 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Broker</th>
                <th className="px-4 py-3 text-left">Verified</th>
                <th className="px-4 py-3 text-left">Subscription</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100" data-testid={`user-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">{r.full_name}</div>
                    <div className="font-mono text-xs text-gray-500">{r.email}</div>
                    <div className="mt-0.5 text-xs text-gray-400">PRC {r.prc_license_number}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={r.is_verified
                      ? "inline-flex items-center rounded-full bg-brand-green/10 px-2.5 py-0.5 text-xs font-semibold text-brand-green"
                      : "inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500"}>
                      {r.is_verified ? "Verified" : "Pending"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    <div>{r.subscription_status || "free"} · {r.subscription_tier || "—"}</div>
                    <div className="text-gray-400">exp {r.subscription_expires_at ? new Date(r.subscription_expires_at).toLocaleDateString() : "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => impersonate(r)}
                        data-testid={`impersonate-btn-${r.id}`}
                      >
                        Log in as
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setOverrideTarget(r); setOverrideKind("pro"); }}
                        data-testid={`grant-pro-btn-${r.id}`}
                      >
                        <CircleDollarSign className="mr-1 h-3 w-3" /> Grant Pro
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setOverrideTarget(r); setOverrideKind("trial"); }}
                        data-testid={`extend-trial-btn-${r.id}`}
                      >
                        <CalendarClock className="mr-1 h-3 w-3" /> Extend trial
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setOverrideTarget(r); setOverrideKind("referral"); }}
                        data-testid={`referral-credit-btn-${r.id}`}
                      >
                        <Gift className="mr-1 h-3 w-3" /> +30d
                      </Button>
                      {canReset && (
                        <>
                          <Button
                            size="sm" variant="outline"
                            disabled={resetBusyId === r.id}
                            onClick={() => sendResetEmail(r)}
                            data-testid={`send-reset-btn-${r.id}`}
                          >
                            {resetBusyId === r.id
                              ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              : <Mail className="mr-1 h-3 w-3" />}
                            Send reset
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            disabled={resetBusyId === r.id}
                            onClick={() => setTempPassword(r)}
                            data-testid={`set-temp-password-btn-${r.id}`}
                          >
                            {resetBusyId === r.id
                              ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              : <KeyRound className="mr-1 h-3 w-3" />}
                            Temp password
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!overrideTarget} onOpenChange={(o) => { if (!o) { setOverrideTarget(null); setOverrideKind(null); } }}>
        <DialogContent className="max-w-md" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle>
              {overrideKind === "pro" && "Grant Pro"}
              {overrideKind === "trial" && "Extend trial"}
              {overrideKind === "referral" && "Grant referral credit"}
            </DialogTitle>
            <DialogDescription>
              {overrideTarget?.full_name} · {overrideTarget?.email}
            </DialogDescription>
          </DialogHeader>
          {overrideKind === "pro" && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500">Tier</label>
                <Select value={overrideForm.tier_id} onValueChange={(v) => setOverrideForm({ ...overrideForm, tier_id: v })}>
                  <SelectTrigger data-testid="override-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tier_monthly">Monthly</SelectItem>
                    <SelectItem value="tier_6mo">Half year</SelectItem>
                    <SelectItem value="tier_annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Months</label>
                <Select value={String(overrideForm.months)} onValueChange={(v) => setOverrideForm({ ...overrideForm, months: v })}>
                  <SelectTrigger data-testid="override-months"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 month</SelectItem>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {overrideKind === "trial" && (
            <div>
              <label className="text-xs font-semibold text-gray-500">Days</label>
              <Input
                type="number" min={1} max={180}
                value={overrideForm.days}
                onChange={(e) => setOverrideForm({ ...overrideForm, days: e.target.value })}
                data-testid="override-days"
              />
            </div>
          )}
          {overrideKind === "referral" && (
            <p className="text-sm text-gray-500">
              Grants a +30 day Pro block (same as a milestone reward). No cost.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOverrideTarget(null); setOverrideKind(null); }} data-testid="override-cancel">
              Cancel
            </Button>
            <Button onClick={applyOverride} disabled={busy} data-testid="override-apply">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!tempPwTarget}
        onOpenChange={(o) => {
          if (!o) {
            setTempPwTarget(null);
            setTempPwValue("");
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand-green" />
              Temporary password created
            </DialogTitle>
            <DialogDescription>
              {tempPwTarget?.full_name} · {tempPwTarget?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Share this password with the broker over a trusted channel.
              It&apos;s shown here <span className="font-semibold">once</span>{" "}
              — after you close this dialog it cannot be retrieved. The
              broker has been notified in-app to change it after signing in.
            </p>
            <div
              className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 font-mono text-sm"
              data-testid="temp-password-value"
            >
              <span className="flex-1 select-all break-all">{tempPwValue}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={copyTempPw}
                data-testid="temp-password-copy"
              >
                <Copy className="mr-1 h-3 w-3" /> Copy
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setTempPwTarget(null);
                setTempPwValue("");
              }}
              data-testid="temp-password-close"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
